import { supabase } from '../supabase';
import { ExporterReceiver, ReceiverAccessSummaryUser } from '../utils/types';

type ExporterReceiversWithUsersResponse = {
  receivers?: ExporterReceiver[];
};

const getReceiversWithUsersByExporter = async (
  exporterId: number
): Promise<ExporterReceiver[]> => {
  const { data, error } = await supabase.rpc(
    'get_exporter_receivers_with_access_users',
    {
      p_exporter_id: exporterId,
    }
  );

  if (error) throw error;

  return ((data as ExporterReceiversWithUsersResponse | null)?.receivers ||
    []) as ExporterReceiver[];
};

const getReceiversWithUsersFallback = async (
  exporterId: number
): Promise<ExporterReceiver[]> => {
  const { data: receivers, error: receiversError } = await supabase
    .from('exporter_receivers')
    .select('*')
    .eq('exporter_id', exporterId);

  if (receiversError) {
    throw new Error(receiversError.message);
  }

  const receiverRows = (receivers || []) as ExporterReceiver[];
  const receiverIds = receiverRows.map((receiver) => receiver.id);

  if (receiverIds.length === 0) {
    return receiverRows;
  }

  const { data: accessRows, error: accessError } = await supabase
    .from('receiver_user_access')
    .select(
      `
        id,
        user_id,
        exporter_receiver_id,
        is_active,
        created_at,
        users:user_id (
          email,
          first_name,
          last_name,
          roles
        )
      `
    )
    .in('exporter_receiver_id', receiverIds);

  if (accessError) {
    throw new Error(accessError.message);
  }

  const usersByReceiverId = new Map<number, ReceiverAccessSummaryUser[]>();
  (
    (accessRows || []) as Array<{
      id: number;
      user_id: number;
      exporter_receiver_id: number;
      is_active: boolean;
      created_at: string;
      users?: {
        email?: string | null;
        first_name?: string | null;
        last_name?: string | null;
        roles?: string[] | null;
      } | null;
    }>
  ).forEach((access) => {
    const current = usersByReceiverId.get(access.exporter_receiver_id) || [];
    current.push({
      id: access.id,
      user_id: access.user_id,
      email: access.users?.email || '',
      first_name: access.users?.first_name,
      last_name: access.users?.last_name,
      roles: access.users?.roles || [],
      is_active: access.is_active,
      created_at: access.created_at,
    });
    usersByReceiverId.set(access.exporter_receiver_id, current);
  });

  return receiverRows.map((receiver) => {
    const users = usersByReceiverId.get(receiver.id) || [];
    return {
      ...receiver,
      receiver_access_users: users,
      receiver_access_user_count: users.length,
    };
  });
};

export const getReceiversByExporter = async (
  exporterId: number
): Promise<ExporterReceiver[]> => {
  try {
    try {
      return await getReceiversWithUsersByExporter(exporterId);
    } catch (rpcError) {
      console.warn(
        'Could not fetch receivers with access users via RPC, falling back:',
        rpcError
      );
      return await getReceiversWithUsersFallback(exporterId);
    }
  } catch (error) {
    console.error('Error fetching receivers:', error);
    throw error;
  }
};

export const addReceiverToExporter = async (
  exporterId: number,
  name: string
): Promise<ExporterReceiver> => {
  try {
    const { data, error } = await supabase
      .from('exporter_receivers')
      .insert([{ exporter_id: exporterId, name }])
      .select()
      .single();

    if (error) {
      throw new Error(error.message);
    }
    return data as ExporterReceiver;
  } catch (error) {
    console.error('Error adding receiver:', error);
    throw error;
  }
};

export const updateReceiver = async (
  id: number,
  name: string
): Promise<ExporterReceiver> => {
  try {
    const { data, error } = await supabase
      .from('exporter_receivers')
      .update({ name })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(error.message);
    }
    return data as ExporterReceiver;
  } catch (error) {
    console.error('Error updating receiver:', error);
    throw error;
  }
};

export const deleteReceiver = async (id: number): Promise<void> => {
  try {
    const { error } = await supabase
      .from('exporter_receivers')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(error.message);
    }
  } catch (error) {
    console.error('Error deleting receiver:', error);
    throw error;
  }
};

/** Maps a legacy receiver name to an official receiver for every shipment and
 *  settlement of the exporter with that name, in every season and species
 *  (RPC set_receiver_mapping_for_shipments; season/species are informational). */
export const setReceiverMappingForShipments = async (params: {
  exporterId: number;
  speciesId: number;
  season: string;
  receiverName: string;
  exporterReceiverId: number;
}): Promise<void> => {
  const { error } = await supabase.rpc('set_receiver_mapping_for_shipments', {
    p_exporter_id: params.exporterId,
    p_species_id: params.speciesId,
    p_season: params.season,
    p_receiver_name: params.receiverName,
    p_exporter_receiver_id: params.exporterReceiverId,
  });
  if (error) throw new Error(error.message);
};
